import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthStore } from '../store/auth.store';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly refreshTokenSubject = new BehaviorSubject<string | null>(null);

  private readonly EXCLUDED_PATTERNS = [
    '/auth/login',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/activate-account',
  ];

  constructor(
    private readonly authStore: AuthStore,
    private readonly authService: AuthService,
    private readonly router: Router,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isExcluded(req.url)) {
      return next.handle(req);
    }

    const token = this.authStore.getAccessToken();
    if (token) {
      req = this.addToken(req, token);
    }

    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          if (this.authStore.getRememberMe()) {
            return this.handle401Refresh(req, next);
          }
          this.authStore.clearSession();
          this.router.navigate(['/auth/login']);
          return throwError(() => err);
        }
        return throwError(() => err);
      }),
    );
  }

  private handle401Refresh(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.authStore.getRefreshToken();
      if (!refreshToken) {
        this.isRefreshing = false;
        this.authStore.clearSession();
        this.router.navigate(['/auth/login']);
        return throwError(() => new Error('No refresh token'));
      }

      return this.authService.refreshToken(refreshToken).pipe(
        switchMap((res: any) => {
          this.isRefreshing = false;
          const newToken = res.data?.accessToken;
          const newRefresh = res.data?.refreshToken;
          if (newToken && newRefresh) {
            this.authStore.updateTokens(newToken, newRefresh);
            this.refreshTokenSubject.next(newToken);
          }
          return next.handle(this.addToken(req, newToken));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.authStore.clearSession();
          this.router.navigate(['/auth/login']);
          return throwError(() => err);
        }),
      );
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addToken(req, token!))),
    );
  }

  private addToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private isExcluded(url: string): boolean {
    return this.EXCLUDED_PATTERNS.some((pattern) => url.includes(pattern));
  }
}
